import React, { useState, useEffect } from 'react';
import {
  Box,
  Card,
  CardContent,
  Typography,
  Button,
  Table,
  TableBody,
  TableCell,
  TableContainer,
  TableHead,
  TableRow,
  Paper,
  TextField,
  FormControl,
  InputLabel,
  Select,
  MenuItem,
  Chip,
  Dialog,
  DialogTitle,
  DialogContent,
  DialogActions,
  Grid,
  InputAdornment,
  IconButton,
  CircularProgress,
  Alert,
  Snackbar,
  Tooltip,
} from '@mui/material';
import {
  Add as AddIcon,
  Edit as EditIcon,
  Search as SearchIcon,
  Refresh as RefreshIcon,
  Business as BusinessIcon,
  OpenInNew as OpenInNewIcon,
} from '@mui/icons-material';
import { useTranslation } from '../../hooks/useTranslation';
import TenantService from '../../services/tenant.service';
import http from '../../http-common';

const TenantManagement = () => {
  const { t } = useTranslation();
  const [tenants, setTenants] = useState([]);
  const [loading, setLoading] = useState(true);
  const [searchTerm, setSearchTerm] = useState('');
  const [statusFilter, setStatusFilter] = useState('');
  const [planFilter, setPlanFilter] = useState('');
  const [isCreateDialogOpen, setIsCreateDialogOpen] = useState(false);
  const [isEditDialogOpen, setIsEditDialogOpen] = useState(false);
  const [selectedTenant, setSelectedTenant] = useState(null);
  const [snackbar, setSnackbar] = useState({ open: false, message: '', severity: 'success' });
  const [formData, setFormData] = useState({
    name: '',
    subdomain: '',
    domain: '',
    nit: '',
    email: '',
    phone: '',
    address: '',
    plan: 'basic',
    profile: 'default',
    status: 'trial',
  });

  useEffect(() => {
    fetchTenants();
  }, [searchTerm, statusFilter, planFilter]);

  const showSnackbar = (message, severity = 'success') => {
    setSnackbar({ open: true, message, severity });
  };

  const isValidSubdomain = (subdomain) => {
    return /^[a-z0-9]([a-z0-9-]*[a-z0-9])?$/.test(subdomain);
  };

  const fetchTenants = async () => {
    try {
      setLoading(true);
      const params = {};
      if (searchTerm) params.search = searchTerm;
      if (statusFilter) params.status = statusFilter;
      if (planFilter) params.plan = planFilter;

      const response = await TenantService.getAll(params);
      const data = response.data?.data || response.data || [];
      setTenants(Array.isArray(data) ? data : []);
    } catch (error) {
      console.error('Error fetching tenants:', error);
      showSnackbar(t('tenantManagement.errorLoadingTenants'), 'error');
    } finally {
      setLoading(false);
    }
  };

  const handleCreateTenant = async (e) => {
    e.preventDefault();

    if (!isValidSubdomain(formData.subdomain)) {
      showSnackbar(t('tenantManagement.invalidSubdomain'), 'error');
      return;
    }

    try {
      await TenantService.create(formData);
      showSnackbar(t('tenantManagement.tenantCreatedSuccess'));
      setIsCreateDialogOpen(false);
      resetForm();
      fetchTenants();
    } catch (error) {
      console.error('Error creating tenant:', error);
      const errorMessage = error.response?.data?.message || t('tenantManagement.errorCreatingTenant');
      showSnackbar(errorMessage, 'error');
    }
  };

  const handleUpdateTenant = async (e) => {
    e.preventDefault();

    if (!isValidSubdomain(formData.subdomain)) {
      showSnackbar(t('tenantManagement.invalidSubdomain'), 'error');
      return;
    }

    try {
      await TenantService.update(selectedTenant.id, formData);
      showSnackbar(t('tenantManagement.tenantUpdatedSuccess'));
      setIsEditDialogOpen(false);
      setSelectedTenant(null);
      resetForm();
      fetchTenants();
    } catch (error) {
      console.error('Error updating tenant:', error);
      const errorMessage = error.response?.data?.message || t('tenantManagement.errorUpdatingTenant');
      showSnackbar(errorMessage, 'error');
    }
  };

  const handleSuspendTenant = async (tenantId) => {
    try {
      await http.post(`/tenants/${tenantId}/suspend`);
      showSnackbar(t('tenantManagement.tenantSuspendedSuccess'));
      fetchTenants();
    } catch (error) {
      console.error('Error suspending tenant:', error);
      showSnackbar(t('tenantManagement.errorSuspendingTenant'), 'error');
    }
  };

  const handleActivateTenant = async (tenantId) => {
    try {
      await http.post(`/tenants/${tenantId}/activate`);
      showSnackbar(t('tenantManagement.tenantActivatedSuccess'));
      fetchTenants();
    } catch (error) {
      console.error('Error activating tenant:', error);
      showSnackbar(t('tenantManagement.errorActivatingTenant'), 'error');
    }
  };

  const openEditDialog = (tenant) => {
    setSelectedTenant(tenant);
    setFormData({
      name: tenant.name || '',
      subdomain: tenant.subdomain || '',
      domain: tenant.domain || '',
      nit: tenant.nit || '',
      email: tenant.email || '',
      phone: tenant.phone || '',
      address: tenant.address || '',
      plan: tenant.plan || 'basic',
      profile: tenant.profile || 'default',
      status: tenant.status || 'trial',
    });
    setIsEditDialogOpen(true);
  };

  const resetForm = () => {
    setFormData({
      name: '',
      subdomain: '',
      domain: '',
      nit: '',
      email: '',
      phone: '',
      address: '',
      plan: 'basic',
      profile: 'default',
      status: 'trial',
    });
  };

  const getStatusColor = (status) => {
    switch (status) {
      case 'active': return 'success';
      case 'trial': return 'info';
      case 'suspended': return 'error';
      case 'cancelled': return 'default';
      default: return 'default';
    }
  };

  const getPlanColor = (plan) => {
    switch (plan) {
      case 'basic': return 'default';
      case 'professional': return 'primary';
      case 'enterprise': return 'secondary';
      default: return 'default';
    }
  };

  const formatDate = (dateString) => {
    if (!dateString) return '-';
    return new Date(dateString).toLocaleDateString();
  };

  return (
    <Box>
      <Card>
        <CardContent>
          <Box display="flex" justifyContent="space-between" alignItems="center" mb={3}>
            <Typography variant="h5" fontWeight="bold">
              {t('tenantManagement.title')}
            </Typography>
            <Box display="flex" gap={2}>
              <IconButton onClick={fetchTenants} color="primary">
                <RefreshIcon />
              </IconButton>
              <Button
                variant="contained"
                startIcon={<AddIcon />}
                onClick={() => {
                  resetForm();
                  setIsCreateDialogOpen(true);
                }}
              >
                {t('tenantManagement.createTenant')}
              </Button>
            </Box>
          </Box>

          {/* Filters */}
          <Box display="flex" gap={2} mb={3}>
            <TextField
              placeholder={t('tenantManagement.searchPlaceholder')}
              value={searchTerm}
              onChange={(e) => setSearchTerm(e.target.value)}
              InputProps={{
                startAdornment: (
                  <InputAdornment position="start">
                    <SearchIcon />
                  </InputAdornment>
                ),
              }}
              sx={{ flexGrow: 1 }}
            />
            <FormControl sx={{ minWidth: 150 }}>
              <InputLabel>{t('tenantManagement.filterByStatus')}</InputLabel>
              <Select
                value={statusFilter}
                onChange={(e) => setStatusFilter(e.target.value)}
                label={t('tenantManagement.filterByStatus')}
              >
                <MenuItem value="">{t('tenantManagement.allStatuses')}</MenuItem>
                <MenuItem value="active">{t('tenantManagement.active')}</MenuItem>
                <MenuItem value="trial">{t('tenantManagement.trial')}</MenuItem>
                <MenuItem value="suspended">{t('tenantManagement.suspended')}</MenuItem>
                <MenuItem value="cancelled">{t('tenantManagement.cancelled')}</MenuItem>
              </Select>
            </FormControl>
            <FormControl sx={{ minWidth: 150 }}>
              <InputLabel>{t('tenantManagement.filterByPlan')}</InputLabel>
              <Select
                value={planFilter}
                onChange={(e) => setPlanFilter(e.target.value)}
                label={t('tenantManagement.filterByPlan')}
              >
                <MenuItem value="">{t('tenantManagement.allPlans')}</MenuItem>
                <MenuItem value="basic">{t('tenantManagement.basic')}</MenuItem>
                <MenuItem value="professional">{t('tenantManagement.professional')}</MenuItem>
                <MenuItem value="enterprise">{t('tenantManagement.enterprise')}</MenuItem>
              </Select>
            </FormControl>
          </Box>

          {/* Tenants Table */}
          {loading ? (
            <Box display="flex" justifyContent="center" p={4}>
              <CircularProgress />
            </Box>
          ) : tenants.length === 0 ? (
            <Alert severity="info">{t('tenantManagement.noTenants')}</Alert>
          ) : (
            <TableContainer component={Paper} variant="outlined">
              <Table>
                <TableHead>
                  <TableRow>
                    <TableCell>{t('tenantManagement.companyName')}</TableCell>
                    <TableCell>{t('tenantManagement.subdomain')}</TableCell>
                    <TableCell>{t('tenantManagement.plan')}</TableCell>
                    <TableCell>{t('tenantManagement.status')}</TableCell>
                    <TableCell>{t('tenantManagement.email')}</TableCell>
                    <TableCell>{t('tenantManagement.createdAt')}</TableCell>
                    <TableCell align="right">{t('common.actions')}</TableCell>
                  </TableRow>
                </TableHead>
                <TableBody>
                  {tenants.map((tenant) => (
                    <TableRow key={tenant.id} hover>
                      <TableCell>
                        <Box display="flex" alignItems="center" gap={1}>
                          <BusinessIcon color="action" />
                          <Typography variant="body2" fontWeight="medium">
                            {tenant.name}
                          </Typography>
                        </Box>
                      </TableCell>
                      <TableCell>
                        <Typography variant="body2">{tenant.subdomain}</Typography>
                      </TableCell>
                      <TableCell>
                        <Chip
                          label={t(`tenantManagement.${tenant.plan}`)}
                          color={getPlanColor(tenant.plan)}
                          size="small"
                        />
                      </TableCell>
                      <TableCell>
                        <Chip
                          label={t(`tenantManagement.${tenant.status}`)}
                          color={getStatusColor(tenant.status)}
                          size="small"
                        />
                      </TableCell>
                      <TableCell>
                        <Typography variant="body2">{tenant.email || '-'}</Typography>
                      </TableCell>
                      <TableCell>
                        <Typography variant="body2">{formatDate(tenant.created_at)}</Typography>
                      </TableCell>
                      <TableCell align="right">
                        <Box display="flex" gap={1} justifyContent="flex-end">
                          <Tooltip title={t('common.edit')}>
                            <IconButton
                              size="small"
                              onClick={() => openEditDialog(tenant)}
                              color="primary"
                            >
                              <EditIcon fontSize="small" />
                            </IconButton>
                          </Tooltip>
                          {tenant.status === 'active' ? (
                            <Tooltip title={t('tenantManagement.suspend')}>
                              <IconButton
                                size="small"
                                onClick={() => handleSuspendTenant(tenant.id)}
                                color="error"
                              >
                                <BusinessIcon fontSize="small" />
                              </IconButton>
                            </Tooltip>
                          ) : (
                            <Tooltip title={t('tenantManagement.activate')}>
                              <IconButton
                                size="small"
                                onClick={() => handleActivateTenant(tenant.id)}
                                color="success"
                              >
                                <BusinessIcon fontSize="small" />
                              </IconButton>
                            </Tooltip>
                          )}
                        </Box>
                      </TableCell>
                    </TableRow>
                  ))}
                </TableBody>
              </Table>
            </TableContainer>
          )}
        </CardContent>
      </Card>

      {/* Create Dialog */}
      <Dialog open={isCreateDialogOpen} onClose={() => setIsCreateDialogOpen(false)} maxWidth="md" fullWidth>
        <form onSubmit={handleCreateTenant}>
          <DialogTitle>{t('tenantManagement.createTenant')}</DialogTitle>
          <DialogContent>
            <Typography variant="body2" color="text.secondary" paragraph>
              {t('tenantManagement.createTenantDescription')}
            </Typography>
            <Grid container spacing={2} sx={{ mt: 1 }}>
              <Grid item xs={12} md={6}>
                <TextField
                  fullWidth
                  label={t('tenantManagement.companyName')}
                  value={formData.name}
                  onChange={(e) => setFormData({ ...formData, name: e.target.value })}
                  required
                />
              </Grid>
              <Grid item xs={12} md={6}>
                <TextField
                  fullWidth
                  label={t('tenantManagement.subdomain')}
                  value={formData.subdomain}
                  onChange={(e) => setFormData({ ...formData, subdomain: e.target.value })}
                  required
                  helperText={t('tenantManagement.subdomainHint')}
                />
              </Grid>
              <Grid item xs={12} md={6}>
                <TextField
                  fullWidth
                  label={t('tenantManagement.customDomain')}
                  value={formData.domain}
                  onChange={(e) => setFormData({ ...formData, domain: e.target.value })}
                  helperText={t('tenantManagement.customDomainHint')}
                />
              </Grid>
              <Grid item xs={12} md={6}>
                <TextField
                  fullWidth
                  label={t('tenantManagement.nit')}
                  value={formData.nit}
                  onChange={(e) => setFormData({ ...formData, nit: e.target.value })}
                />
              </Grid>
              <Grid item xs={12} md={6}>
                <TextField
                  fullWidth
                  label={t('tenantManagement.email')}
                  type="email"
                  value={formData.email}
                  onChange={(e) => setFormData({ ...formData, email: e.target.value })}
                />
              </Grid>
              <Grid item xs={12} md={6}>
                <TextField
                  fullWidth
                  label={t('tenantManagement.phone')}
                  value={formData.phone}
                  onChange={(e) => setFormData({ ...formData, phone: e.target.value })}
                />
              </Grid>
              <Grid item xs={12} md={6}>
                <FormControl fullWidth>
                  <InputLabel>{t('tenantManagement.plan')}</InputLabel>
                  <Select
                    value={formData.plan}
                    onChange={(e) => setFormData({ ...formData, plan: e.target.value })}
                    label={t('tenantManagement.plan')}
                  >
                    <MenuItem value="basic">{t('tenantManagement.basic')}</MenuItem>
                    <MenuItem value="professional">{t('tenantManagement.professional')}</MenuItem>
                    <MenuItem value="enterprise">{t('tenantManagement.enterprise')}</MenuItem>
                  </Select>
                </FormControl>
              </Grid>
              <Grid item xs={12} md={6}>
                <FormControl fullWidth>
                  <InputLabel>{t('tenantManagement.profile')}</InputLabel>
                  <Select
                    value={formData.profile}
                    onChange={(e) => setFormData({ ...formData, profile: e.target.value })}
                    label={t('tenantManagement.profile')}
                  >
                    <MenuItem value="default">{t('tenantManagement.profileDefault')}</MenuItem>
                    <MenuItem value="school">{t('tenantManagement.profileSchool')}</MenuItem>
                    <MenuItem value="loyalty">{t('tenantManagement.profileLoyalty')}</MenuItem>
                    <MenuItem value="menu">{t('tenantManagement.profileMenu')}</MenuItem>
                  </Select>
                </FormControl>
              </Grid>
              <Grid item xs={12} md={6}>
                <FormControl fullWidth>
                  <InputLabel>{t('tenantManagement.status')}</InputLabel>
                  <Select
                    value={formData.status}
                    onChange={(e) => setFormData({ ...formData, status: e.target.value })}
                    label={t('tenantManagement.status')}
                  >
                    <MenuItem value="trial">{t('tenantManagement.trial')}</MenuItem>
                    <MenuItem value="active">{t('tenantManagement.active')}</MenuItem>
                    <MenuItem value="suspended">{t('tenantManagement.suspended')}</MenuItem>
                    <MenuItem value="cancelled">{t('tenantManagement.cancelled')}</MenuItem>
                  </Select>
                </FormControl>
              </Grid>
              <Grid item xs={12}>
                <TextField
                  fullWidth
                  label={t('tenantManagement.address')}
                  value={formData.address}
                  onChange={(e) => setFormData({ ...formData, address: e.target.value })}
                  multiline
                  rows={2}
                />
              </Grid>
            </Grid>
          </DialogContent>
          <DialogActions>
            <Button onClick={() => setIsCreateDialogOpen(false)}>{t('common.cancel')}</Button>
            <Button type="submit" variant="contained">
              {t('common.create')}
            </Button>
          </DialogActions>
        </form>
      </Dialog>

      {/* Edit Dialog */}
      <Dialog open={isEditDialogOpen} onClose={() => setIsEditDialogOpen(false)} maxWidth="md" fullWidth>
        <form onSubmit={handleUpdateTenant}>
          <DialogTitle>{t('tenantManagement.editTenant')}</DialogTitle>
          <DialogContent>
            <Typography variant="body2" color="text.secondary" paragraph>
              {t('tenantManagement.editTenantDescription')}
            </Typography>
            <Grid container spacing={2} sx={{ mt: 1 }}>
              <Grid item xs={12} md={6}>
                <TextField
                  fullWidth
                  label={t('tenantManagement.companyName')}
                  value={formData.name}
                  onChange={(e) => setFormData({ ...formData, name: e.target.value })}
                  required
                />
              </Grid>
              <Grid item xs={12} md={6}>
                <TextField
                  fullWidth
                  label={t('tenantManagement.subdomain')}
                  value={formData.subdomain}
                  onChange={(e) => setFormData({ ...formData, subdomain: e.target.value })}
                  required
                />
              </Grid>
              <Grid item xs={12} md={6}>
                <TextField
                  fullWidth
                  label={t('tenantManagement.customDomain')}
                  value={formData.domain}
                  onChange={(e) => setFormData({ ...formData, domain: e.target.value })}
                />
              </Grid>
              <Grid item xs={12} md={6}>
                <TextField
                  fullWidth
                  label={t('tenantManagement.nit')}
                  value={formData.nit}
                  onChange={(e) => setFormData({ ...formData, nit: e.target.value })}
                />
              </Grid>
              <Grid item xs={12} md={6}>
                <TextField
                  fullWidth
                  label={t('tenantManagement.email')}
                  type="email"
                  value={formData.email}
                  onChange={(e) => setFormData({ ...formData, email: e.target.value })}
                />
              </Grid>
              <Grid item xs={12} md={6}>
                <TextField
                  fullWidth
                  label={t('tenantManagement.phone')}
                  value={formData.phone}
                  onChange={(e) => setFormData({ ...formData, phone: e.target.value })}
                />
              </Grid>
              <Grid item xs={12} md={6}>
                <FormControl fullWidth>
                  <InputLabel>{t('tenantManagement.plan')}</InputLabel>
                  <Select
                    value={formData.plan}
                    onChange={(e) => setFormData({ ...formData, plan: e.target.value })}
                    label={t('tenantManagement.plan')}
                  >
                    <MenuItem value="basic">{t('tenantManagement.basic')}</MenuItem>
                    <MenuItem value="professional">{t('tenantManagement.professional')}</MenuItem>
                    <MenuItem value="enterprise">{t('tenantManagement.enterprise')}</MenuItem>
                  </Select>
                </FormControl>
              </Grid>
              <Grid item xs={12} md={6}>
                <FormControl fullWidth>
                  <InputLabel>{t('tenantManagement.profile')}</InputLabel>
                  <Select
                    value={formData.profile}
                    onChange={(e) => setFormData({ ...formData, profile: e.target.value })}
                    label={t('tenantManagement.profile')}
                  >
                    <MenuItem value="default">{t('tenantManagement.profileDefault')}</MenuItem>
                    <MenuItem value="school">{t('tenantManagement.profileSchool')}</MenuItem>
                    <MenuItem value="loyalty">{t('tenantManagement.profileLoyalty')}</MenuItem>
                    <MenuItem value="menu">{t('tenantManagement.profileMenu')}</MenuItem>
                  </Select>
                </FormControl>
              </Grid>
              <Grid item xs={12} md={6}>
                <FormControl fullWidth>
                  <InputLabel>{t('tenantManagement.status')}</InputLabel>
                  <Select
                    value={formData.status}
                    onChange={(e) => setFormData({ ...formData, status: e.target.value })}
                    label={t('tenantManagement.status')}
                  >
                    <MenuItem value="trial">{t('tenantManagement.trial')}</MenuItem>
                    <MenuItem value="active">{t('tenantManagement.active')}</MenuItem>
                    <MenuItem value="suspended">{t('tenantManagement.suspended')}</MenuItem>
                    <MenuItem value="cancelled">{t('tenantManagement.cancelled')}</MenuItem>
                  </Select>
                </FormControl>
              </Grid>
              <Grid item xs={12}>
                <TextField
                  fullWidth
                  label={t('tenantManagement.address')}
                  value={formData.address}
                  onChange={(e) => setFormData({ ...formData, address: e.target.value })}
                  multiline
                  rows={2}
                />
              </Grid>
            </Grid>
          </DialogContent>
          <DialogActions>
            <Button onClick={() => setIsEditDialogOpen(false)}>{t('common.cancel')}</Button>
            <Button type="submit" variant="contained">
              {t('common.save')}
            </Button>
          </DialogActions>
        </form>
      </Dialog>

      {/* Snackbar */}
      <Snackbar
        open={snackbar.open}
        autoHideDuration={6000}
        onClose={() => setSnackbar({ ...snackbar, open: false })}
      >
        <Alert
          onClose={() => setSnackbar({ ...snackbar, open: false })}
          severity={snackbar.severity}
        >
          {snackbar.message}
        </Alert>
      </Snackbar>
    </Box>
  );
};

export default TenantManagement;
